import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, take, takeUntil } from 'rxjs';
import { CanDeactivateType } from '../../../../../guards/form.guard';
import {
  SeminarConditionModel,
  SeminarConditionType,
  SeminarCreateUpdateModel,
  SeminarDateCreateUpdateModel,
  SeminarDateSeriesCreateUpdateModel,
  SeminarModel,
} from '../../../../../models/seminar.model';
import { SponsorCardModel } from '../../../../../models/sponsor-card.model';
import { TrainingCategoryModel } from '../../../../../models/training-category.model';
import { UserModel } from '../../../../../models/user.model';
import { AlertService } from '../../../../../services/alert.service';
import { FileManagerStateService } from '../../../../../services/file-manager-state.service';
import { FormService } from '../../../../../services/form.service';
import { SeminarService } from '../../../../../services/seminar.service';
import { SponsorCardService } from '../../../../../services/sponsor-card.service';
import { TrainingCategoryService } from '../../../../../services/training-category.service';
import { UserService } from '../../../../../services/user.service';
import { getCourseDuration as getSeminarDuration } from '../../../../../utils/course.utils';
import {
  fieldRequired,
  getMaxLengthFromValidator,
  getTextAreaLength,
  hasUnsavedChanges,
} from '../../../../../utils/form.utils';
import { hasValidSeminarPrice } from '../../../../../utils/seminar.utils';
import { houseNumberValidator } from '../../../../../validators/house-number.validator';
import { numberFormatValidator } from '../../../../../validators/number-format.validator';
import { CancelSeminarDialogComponent } from '../../../../shared-components/cancel-seminar-dialog/cancel-seminar-dialog.component';
import { ConfirmDialogComponent } from '../../../../shared-components/confirm-dialog/confirm-dialog.component';

interface SelectModel {
  label: string;
  value: string | number;
}

const PRICE_CONTROL_MAP: Record<
  SeminarConditionType,
  { group: string; controls: string[] }
> = {
  [SeminarConditionType.REGULAR]: {
    group: 'regularPriceGroup',
    controls: ['price'],
  },
  [SeminarConditionType.EARLY]: {
    group: 'earlyBirdPriceGroup',
    controls: ['price', 'date'],
  },
  [SeminarConditionType.BED]: {
    group: 'bedPriceGroup',
    controls: ['price'],
  },
  [SeminarConditionType.LOGO]: {
    group: 'logoPriceGroup',
    controls: ['price'],
  },
  [SeminarConditionType.OFFICE]: {
    group: 'groupPriceGroup',
    controls: ['price'],
  },
  [SeminarConditionType.VDB]: {
    group: 'vdbPriceGroup',
    controls: ['price'],
  },
  [SeminarConditionType.VDP]: {
    group: 'vdpPriceGroup',
    controls: ['price'],
  },
};

@Component({
  selector: 'app-seminars-formular',
  templateUrl: './seminars-formular.component.html',
  styleUrl: './seminars-formular.component.scss',
  providers: [FileManagerStateService],
  standalone: false,
})
export class SeminarsFormularComponent implements OnInit, OnDestroy {
  /* add window.onbeforeunload to warn the user if the form has unsaved changes */
  @HostListener('window:beforeunload', ['$event'])
  public reloadNotification($event: any): void {
    if (hasUnsavedChanges(this.initialFormValues, this.seminarForm.value)) {
      $event.returnValue =
        'Es gibt ungespeicherte Änderungen. Wenn Sie die Seite verlassen, gehen Daten verloren.';
    }
  }

  // Constructor
  constructor(
    private location: Location,
    private userService: UserService,
    private courseTypeService: TrainingCategoryService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialogService: DialogService,
    private formService: FormService,
    private alertService: AlertService,
    private seminarService: SeminarService,
    private fileManagerState: FileManagerStateService,
    private sponsorCardService: SponsorCardService
  ) {
    this.createForm();
  }

  // Variables
  public isLoading: boolean = true;
  public editMode: boolean = false;
  public previewMode: boolean = false;
  public seminar: SeminarModel;
  public initialFormValues: any;
  private destroy$ = new Subject<void>();

  public seminarForm: FormGroup;

  private lecturers: UserModel[];
  private categories: TrainingCategoryModel[];

  public lecturerOptions: SelectModel[];
  public categoryOptions: SelectModel[];
  public sponsorCardOptions: SponsorCardModel[];

  public selectedSponsorCards: SponsorCardModel[] = [];

  public fieldRequired = fieldRequired;
  public getTextAreaLength = getTextAreaLength;
  public getMaxLengthFromValidator = getMaxLengthFromValidator;

  SeminarConditionType = SeminarConditionType;

  public contextMenuItems = [];

  private updateContextMenuItems(): void {
    const items = [];

    if (this.seminar && !this.seminar.isCanceled) {
      items.push({
        label: 'Absagen',
        icon: 'fa-regular fa-times',
        command: () => {
          this.cancelSeminar();
        },
      });
    }

    items.push({
      label: 'Löschen',
      icon: 'fa-regular fa-trash-can',
      command: () => {
        this.onDeleteSeminar();
      },
    });

    this.contextMenuItems = items;
  }

  public paypalPaymentAvailableOptions = [
    { label: 'Ja', value: true },
    { label: 'Nein', value: false },
  ];

  public async ngOnInit() {
    this.isLoading = true;
    await Promise.all([
      this.getAllLecturers(),
      this.getCategories(),
      this.getSponsorCards(),
    ]);
    const seminarIdParam = this.activatedRoute.snapshot.params['courseId'];
    const duplicateFromParam =
      this.activatedRoute.snapshot.queryParamMap.get('duplicateFrom');
    if (seminarIdParam) {
      this.editMode = true;
      await this.getSeminar(+atob(seminarIdParam));
    } else if (duplicateFromParam) {
      await this.loadDuplicateFromSeminar(+atob(duplicateFromParam));
    }
    this.initialFormValues = JSON.parse(JSON.stringify(this.seminarForm.value));
    this.isLoading = false;
  }

  private async getAllLecturers(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.userService
        .getAllLecturers()
        .pipe(take(1))
        .subscribe({
          next: users => {
            this.lecturers = users;
            this.lecturerOptions = users?.map(user => {
              return {
                label: `${user.firstname} ${user.lastname}`,
                value: user.id,
              };
            });
            resolve();
          },
          error: error => {
            console.error('Error fetching lecturers', error);
            this.alertService.showErrorAlert(
              'Die Dozenten konnten nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  private async getSponsorCards(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.sponsorCardService
        .getSponsorCards()
        .pipe(take(1))
        .subscribe({
          next: sponsorCards => {
            this.sponsorCardOptions = sponsorCards;
            resolve();
          },
          error: () => {
            this.alertService.showErrorAlert(
              'Die Sponsoren konnten nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  public getLecturer2Options(): SelectModel[] {
    return this.lecturerOptions.filter(
      lecturer => lecturer.value !== this.seminarForm.get('lecturer').value
    );
  }

  public getCategory2Options(): SelectModel[] {
    return this.categoryOptions.filter(
      category => category.value !== this.seminarForm.get('category').value
    );
  }

  private async getCategories(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.courseTypeService
        .getAllTrainingCategories(true)
        .pipe(take(1))
        .subscribe({
          next: categories => {
            this.categories = categories;
            this.categoryOptions = categories.map(category => ({
              label: category.name,
              value: category.id,
            }));
            resolve();
          },
          error: error => {
            console.error('Error fetching categories', error);
            this.alertService.showErrorAlert(
              'Die Kategorien konnten nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  private async getSeminar(seminarId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.seminarService
        .getSeminarById(seminarId, true)
        .pipe(take(1))
        .subscribe({
          next: seminar => {
            this.seminar = seminar;
            this.patchFormValues(seminar);
            this.updateContextMenuItems();
            resolve();
          },
          error: error => {
            console.error('Error fetching seminar', error);
            this.alertService.showErrorAlert(
              'Das Seminar konnte nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  private async loadDuplicateFromSeminar(seminarId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.seminarService
        .getSeminarById(seminarId, true)
        .pipe(take(1))
        .subscribe({
          next: seminar => {
            const template: SeminarModel = {
              ...seminar,
              id: undefined,
              seminarNr: undefined,
              dateSeries: [],
            };
            this.patchFormValues(template);
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: {},
              replaceUrl: true,
            });
            resolve();
          },
          error: error => {
            console.error('Error fetching seminar for duplicate', error);
            this.alertService.showErrorAlert(
              'Das Seminar konnte nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  private patchFormValues(seminar: SeminarModel) {
    this.seminarForm.patchValue({
      name: seminar.title,
      lecturer: seminar.lecturer1?.id,
      lecturer2: seminar.lecturer2?.id,
      category: seminar.category1?.id,
      category2: seminar.category2?.id,
      amount: seminar.maxParticipants,
      recommendation: seminar.recommendation?.trim(),
      requirements: seminar.requirement?.trim(),
      modality: seminar.modality,
      link: seminar.link?.trim(),
      platform: seminar.platform?.trim() ?? null,
      meetingId: seminar.meetingId?.trim() ?? null,
      meetingPassword: seminar.meetingPassword?.trim() ?? null,
      addressName: seminar.address?.title?.trim(),
      addressStreet: seminar.address?.street?.trim(),
      addressHouseNr: seminar.address?.houseNumber?.trim(),
      addressZip: seminar.address?.zip?.trim(),
      addressCity: seminar.address?.city?.trim(),
      description: seminar.description?.trim(),
      purposeExplanation: seminar.about?.trim(),
      goals: seminar.goals?.trim(),
      purpose: seminar.benefit?.trim(),
      courseNumber: seminar.seminarNr,
      fobiPoints: seminar.fobiPoints,
      paypalPaymentAvailable: seminar.paypalPaymentAvailable,
      additionalInfoTitle: seminar.additionsTitle?.trim(),
      additionalInfoDescription: seminar.additionsDescription?.trim(),
      questionsText: seminar.specialInterest?.trim(),
      sponsorCards: seminar.sponsorCardIds,
    });

    [
      SeminarConditionType.REGULAR,
      SeminarConditionType.EARLY,
      SeminarConditionType.BED,
      SeminarConditionType.LOGO,
      SeminarConditionType.OFFICE,
      SeminarConditionType.VDB,
      SeminarConditionType.VDP,
    ].forEach(type => this.patchPriceCondition(seminar, type));

    this.dateGroups.clear();
    // Add date groups
    seminar.dateSeries?.forEach(dateGroup => {
      const group = new FormGroup({
        id: new FormControl(dateGroup.id),
        name: new FormControl(dateGroup.name),
        dates: new FormArray([]),
      });
      this.dateGroups.push(group);

      // Add dates to date group
      dateGroup.dates?.forEach(date => {
        const dateForm = new FormGroup({
          date: new FormControl(date.startDate),
          startTime: new FormControl(moment(date.startDate).format('HH:mm')),
          endTime: new FormControl(moment(date.endDate).format('HH:mm')),
          duration: new FormControl(
            getSeminarDuration(date.startDate, date.endDate)
          ),
          fobiPoints: new FormControl(
            String(date.fobiPoints ?? '').padStart(2, '0')
          ),
        });
        this.getDatesArray(this.dateGroups.length - 1).push(dateForm);
      });
    });
    if (!seminar.dateSeries || seminar.dateSeries.length === 0) {
      this.addDateGroup();
    }
  }

  private createForm() {
    this.seminarForm = new FormGroup({
      name: new FormControl('', Validators.required),
      lecturer: new FormControl(null, Validators.required),
      lecturer2: new FormControl(null),
      category: new FormControl(null, Validators.required),
      category2: new FormControl(null),
      amount: new FormControl(null, Validators.required),
      recommendation: new FormControl('', [
        Validators.required,
        Validators.maxLength(1200),
      ]),
      requirements: new FormControl('', [Validators.maxLength(1200)]),
      modality: new FormControl(null, Validators.required),
      link: new FormControl(''),
      platform: new FormControl(null),
      meetingId: new FormControl(null),
      meetingPassword: new FormControl(null),
      addressName: new FormControl(''),
      addressStreet: new FormControl(''),
      addressHouseNr: new FormControl(''),
      addressZip: new FormControl(''),
      addressCity: new FormControl(''),
      description: new FormControl('', [
        Validators.required,
        Validators.maxLength(3500),
      ]),
      purposeExplanation: new FormControl('', [
        Validators.required,
        Validators.maxLength(3500),
      ]),
      goals: new FormControl('', [
        Validators.required,
        Validators.maxLength(3500),
      ]),
      purpose: new FormControl('', [
        Validators.required,
        Validators.maxLength(3500),
      ]),
      courseNumber: new FormControl('', Validators.required),
      fobiPoints: new FormControl('', Validators.required),
      regularPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
      }),
      earlyBirdPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
        date: new FormControl<string>(null),
      }),
      bedPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
      }),
      logoPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
      }),
      vdbPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
      }),
      vdpPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
      }),
      groupPriceGroup: new FormGroup({
        id: new FormControl(null),
        checkbox: new FormControl(false),
        price: new FormControl<number>(
          { value: null, disabled: true },
          numberFormatValidator()
        ),
      }),
      paypalPaymentAvailable: new FormControl(true),
      additionalInfoTitle: new FormControl('', [
        Validators.required,
        Validators.maxLength(1200),
      ]),
      additionalInfoDescription: new FormControl('', [
        Validators.required,
        Validators.maxLength(1200),
      ]),
      questionsText: new FormControl('', [
        Validators.required,
        Validators.maxLength(1200),
      ]),
      dateGroups: new FormArray([]),
      sponsorCards: new FormControl(null),
    });
    this.addDateGroup();

    this.subscribeToModalityChanges();
  }

  private subscribeToModalityChanges() {
    this.seminarForm
      .get('modality')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value === 'online') {
          this.seminarForm.get('link').setValidators(Validators.required);
          this.seminarForm.get('addressName').setValidators(null);
          this.seminarForm.get('addressStreet').setValidators(null);
          this.seminarForm.get('addressHouseNr').setValidators(null);
          this.seminarForm.get('addressZip').setValidators(null);
          this.seminarForm.get('addressCity').setValidators(null);
        } else {
          this.seminarForm.get('link').setValidators(null);
          this.seminarForm
            .get('addressName')
            .setValidators(Validators.required);
          this.seminarForm
            .get('addressStreet')
            .setValidators(Validators.required);
          this.seminarForm
            .get('addressHouseNr')
            .setValidators([Validators.required, houseNumberValidator()]);
          this.seminarForm.get('addressZip').setValidators(Validators.required);
          this.seminarForm
            .get('addressCity')
            .setValidators(Validators.required);
        }
        this.seminarForm.get('link').updateValueAndValidity();
        this.seminarForm.get('addressName').updateValueAndValidity();
        this.seminarForm.get('addressStreet').updateValueAndValidity();
        this.seminarForm.get('addressHouseNr').updateValueAndValidity();
        this.seminarForm.get('addressZip').updateValueAndValidity();
        this.seminarForm.get('addressCity').updateValueAndValidity();
      });
  }

  private patchPriceCondition(
    seminar: SeminarModel,
    type: SeminarConditionType
  ): void {
    const condition = seminar.conditions?.find(item => item.type === type);
    if (!condition) {
      return;
    }

    const value: {
      id: number | null;
      price: number;
      checkbox: boolean;
      date?: Date | null;
    } = {
      id: condition.id ?? null,
      price: condition.price,
      checkbox: true,
    };

    if (type === SeminarConditionType.EARLY) {
      value.date = condition.date ? moment(condition.date).toDate() : null;
    }

    this.seminarForm.get(PRICE_CONTROL_MAP[type].group).patchValue(value);
    this.updateConditionValidators(type, { target: { checked: true } });
  }

  public updateConditionValidators(
    condition: SeminarConditionType,
    event: any
  ) {
    const config = PRICE_CONTROL_MAP[condition];
    if (!config) return;

    const formGroup = this.seminarForm.get(config.group) as FormGroup;
    if (!formGroup) return;

    const setValidatorsAndState = (
      control: AbstractControl,
      controlName: string,
      required: boolean
    ) => {
      control.setValidators(
        required
          ? [Validators.required].concat(
              controlName === 'date' ? [] : [numberFormatValidator()]
            )
          : null
      );
      control.updateValueAndValidity();
      control[required ? 'enable' : 'disable']();
    };

    config.controls.forEach(controlName =>
      setValidatorsAndState(
        formGroup.get(controlName),
        controlName,
        event.target.checked
      )
    );
  }

  get dateGroups(): FormArray {
    return this.seminarForm.get('dateGroups') as FormArray;
  }

  public addDateGroup(): void {
    this.dateGroups.push(
      new FormGroup({
        id: new FormControl(null),
        name: new FormControl(null, [Validators.required]),
        dates: new FormArray([
          new FormGroup({
            date: new FormControl(null, [Validators.required]),
            startTime: new FormControl(null, [Validators.required]),
            endTime: new FormControl(null, [Validators.required]),
            duration: new FormControl(null),
            fobiPoints: new FormControl(null),
          }),
        ]),
      })
    );
  }

  public removeDateGroup(index: number): void {
    this.dateGroups.removeAt(index);
  }

  // Getter for dates FormArray within a specific date group
  public getDatesArray(dateGroupIndex: number): FormArray {
    return this.dateGroups.at(dateGroupIndex).get('dates') as FormArray;
  }

  // Add a new date entry (FormGroup with date, startTime, and endTime)
  public addDateToGroup(dateGroup: AbstractControl, dateIndex: number) {
    const datesArray = dateGroup.get('dates') as FormArray;
    const date = new FormGroup({
      date: new FormControl(null, [Validators.required]),
      startTime: new FormControl(null, [Validators.required]),
      endTime: new FormControl(null, [Validators.required]),
      duration: new FormControl(null),
      fobiPoints: new FormControl(null),
    });
    datesArray.insert(dateIndex + 1, date);
  }

  // Remove a date entry from a specific date group
  public removeDateFromGroup(dateGroup: AbstractControl, dateIndex: number) {
    const datesArray = dateGroup.get('dates') as FormArray;
    datesArray.removeAt(dateIndex);
  }

  public calculateDuration(dateGroup: AbstractControl) {
    // check if startTime and endTime are set
    const startTime = dateGroup.get('startTime').value;
    const endTime = dateGroup.get('endTime').value;

    if (!startTime || !endTime) {
      return;
    }

    // calculate duration
    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');
    const duration = moment.duration(end.diff(start));

    if (duration.minutes() < 0 || duration.hours() < 0) {
      return;
    }

    const hours = String(duration.hours()).padStart(2, '0');
    const minutes = String(duration.minutes()).padStart(2, '0');
    dateGroup.get('duration').setValue(`${hours}:${minutes}`);

    // calculate fobi points
    const fobiPoints = this.calculateFobiPoints(duration);
    dateGroup.get('fobiPoints').setValue(fobiPoints);
  }

  private calculateFobiPoints(duration: moment.Duration) {
    const totalMinutes = duration.asMinutes();
    return Math.floor(totalMinutes / 45)
      .toString()
      .padStart(2, '0');
  }

  public onCancel(): void {
    this.location.back();
  }

  public onPublish(): void {
    if (this.seminarForm.invalid) {
      this.formService.markFormGroupTouched(this.seminarForm);
      this.alertService.showErrorAlert(
        'Es gibt noch unausgefüllte Pflichtfelder.'
      );
      this.previewMode = false;
      return;
    }

    const seminarCreateUpdateModel: SeminarCreateUpdateModel =
      this.buildSeminarCreateUpdateModel(false);
    if (!hasValidSeminarPrice(seminarCreateUpdateModel)) {
      this.alertService.showErrorAlert(
        'Mindestens ein Preis muss ausgefüllt werden.'
      );
      this.previewMode = false;
      return;
    }

    this.editMode
      ? this.updateSeminar(seminarCreateUpdateModel)
      : this.createSeminar(seminarCreateUpdateModel);
  }

  public onDraft(): void {
    const seminarCreateUpdateModel: SeminarCreateUpdateModel =
      this.buildSeminarCreateUpdateModel(true);
    if (this.seminar && !this.seminar.isDraft) {
      const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
        width: '475px',
        contentStyle: { overflow: 'auto' },
        breakpoints: {
          '640px': '90vw',
        },
        data: {
          title: 'Als Entwurf speichern',
          message:
            'Dein Seminar wird vom Online-Status in den Entwurf-Status wechseln und ist damit nicht mehr für neue Nutzer zur Anmeldung sichtbar.',
          icon: 'fa-regular fa-floppy-disk',
          confirmButtonText: 'Anwenden',
        },
      });
      dialogRef.onClose.subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.editMode
            ? this.updateSeminar(seminarCreateUpdateModel)
            : this.createSeminar(seminarCreateUpdateModel);
        }
      });
    } else {
      this.editMode
        ? this.updateSeminar(seminarCreateUpdateModel)
        : this.createSeminar(seminarCreateUpdateModel);
    }
  }

  private createSeminar(seminar: SeminarCreateUpdateModel) {
    this.isLoading = true;
    this.seminarService
      .createSeminar(seminar)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.initialFormValues = JSON.parse(
            JSON.stringify(this.seminarForm.value)
          );
          this.location.back();
        },
        error: error => {
          this.isLoading = false;
          console.error('Error creating seminar', error);
          this.alertService.showErrorAlert(
            'Das Seminar konnte nicht erstellt werden.'
          );
        },
      });
  }

  private updateSeminar(seminar: SeminarCreateUpdateModel) {
    this.isLoading = true;
    this.seminarService
      .updateSeminar(seminar)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.initialFormValues = JSON.parse(
            JSON.stringify(this.seminarForm.value)
          );
          this.location.back();
        },
        error: error => {
          this.isLoading = false;
          console.error('Error updating seminar', error);
          this.alertService.showErrorAlert(
            'Das Seminar konnte nicht aktualisiert werden.'
          );
        },
      });
  }

  private buildSeminarCreateUpdateModel(
    isDraft: boolean
  ): SeminarCreateUpdateModel {
    let conditions: SeminarConditionModel[] = [];
    if (
      this.seminarForm.get('regularPriceGroup').get('checkbox').value === true
    ) {
      conditions?.push({
        id: this.seminarForm.get('regularPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.REGULAR,
        price: this.seminarForm.get('regularPriceGroup').get('price').value,
      });
    }
    if (
      this.seminarForm.get('earlyBirdPriceGroup').get('checkbox').value === true
    ) {
      conditions?.push({
        id: this.seminarForm.get('earlyBirdPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.EARLY,
        price: this.seminarForm.get('earlyBirdPriceGroup').get('price').value,
        date: this.seminarForm.get('earlyBirdPriceGroup').get('date').value
          ? moment(
              this.seminarForm.get('earlyBirdPriceGroup').get('date').value
            ).format('YYYY-MM-DD HH:mm:ss')
          : null,
      });
    }
    if (this.seminarForm.get('bedPriceGroup').get('checkbox').value === true) {
      conditions?.push({
        id: this.seminarForm.get('bedPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.BED,
        price: this.seminarForm.get('bedPriceGroup').get('price').value,
      });
    }
    if (this.seminarForm.get('logoPriceGroup').get('checkbox').value === true) {
      conditions?.push({
        id: this.seminarForm.get('logoPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.LOGO,
        price: this.seminarForm.get('logoPriceGroup').get('price').value,
      });
    }
    if (this.seminarForm.get('vdbPriceGroup').get('checkbox').value === true) {
      conditions?.push({
        id: this.seminarForm.get('vdbPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.VDB,
        price: this.seminarForm.get('vdbPriceGroup').get('price').value,
      });
    }
    if (this.seminarForm.get('vdpPriceGroup').get('checkbox').value === true) {
      conditions?.push({
        id: this.seminarForm.get('vdpPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.VDP,
        price: this.seminarForm.get('vdpPriceGroup').get('price').value,
      });
    }
    if (
      this.seminarForm.get('groupPriceGroup').get('checkbox').value === true
    ) {
      conditions?.push({
        id: this.seminarForm.get('groupPriceGroup').get('id').value ?? null,
        type: SeminarConditionType.OFFICE,
        price: this.seminarForm.get('groupPriceGroup').get('price').value,
      });
    }
    return {
      id: this.seminar?.id || null,
      lecturerId: this.seminarForm.get('lecturer').value,
      lecturer2Id: this.seminarForm.get('lecturer2').value,
      categoryId: this.seminarForm.get('category').value,
      category2Id: this.seminarForm.get('category2').value,
      title: this.seminarForm.get('name').value?.trim(),
      maxParticipants: this.seminarForm.get('amount').value,
      recommendation: this.seminarForm.get('recommendation').value?.trim(),
      requirement: this.seminarForm.get('requirements').value?.trim(),
      modality: this.seminarForm.get('modality').value,
      link:
        this.seminarForm.get('modality').value === 'online'
          ? this.seminarForm.get('link').value?.trim()
          : null,
      platform:
        this.seminarForm.get('modality').value === 'online'
          ? this.seminarForm.get('platform').value?.trim()
          : null,
      meetingId:
        this.seminarForm.get('modality').value === 'online'
          ? this.seminarForm.get('meetingId').value?.trim()
          : null,
      meetingPassword:
        this.seminarForm.get('modality').value === 'online'
          ? this.seminarForm.get('meetingPassword').value?.trim()
          : null,
      address:
        this.seminarForm.get('modality').value === 'offline'
          ? {
              title: this.seminarForm.get('addressName').value?.trim(),
              street: this.seminarForm.get('addressStreet').value?.trim(),
              houseNumber: this.seminarForm.get('addressHouseNr').value?.trim(),
              zip: this.seminarForm.get('addressZip').value?.trim(),
              city: this.seminarForm.get('addressCity').value?.trim(),
            }
          : null,
      description: this.seminarForm.get('description').value?.trim(),
      about: this.seminarForm.get('purposeExplanation').value?.trim(),
      goals: this.seminarForm.get('goals').value?.trim(),
      benefit: this.seminarForm.get('purpose').value?.trim(),
      seminarNr: this.seminarForm.get('courseNumber').value?.trim(),
      fobiPoints: this.seminarForm.get('fobiPoints').value,
      conditions: conditions,
      paypalPaymentAvailable: this.seminarForm.get('paypalPaymentAvailable')
        .value,
      additionsTitle: this.seminarForm.get('additionalInfoTitle').value?.trim(),
      additionsDescription: this.seminarForm
        .get('additionalInfoDescription')
        .value?.trim(),
      specialInterest: this.seminarForm.get('questionsText').value?.trim(),
      isDraft: isDraft,
      dateSeries: this.seminarForm
        .get('dateGroups')
        .value.map(
          (group: any): SeminarDateSeriesCreateUpdateModel => ({
            id: group.id ?? null,
            name: group.name,
            dates: group.dates
              .map(
                (date: any): SeminarDateCreateUpdateModel => ({
                  id: date.id ?? null,
                  startDate: date.date
                    ? moment(date.date)
                        .set({
                          hour: moment(date.startTime, 'HH:mm').hour(),
                          minute: moment(date.startTime, 'HH:mm').minute(),
                        })
                        .format('YYYY-MM-DD HH:mm:ss')
                    : null,
                  endDate: date.date
                    ? moment(date.date)
                        .set({
                          hour: moment(date.endTime, 'HH:mm').hour(),
                          minute: moment(date.endTime, 'HH:mm').minute(),
                        })
                        .format('YYYY-MM-DD HH:mm:ss')
                    : null,
                  fobiPoints: date.fobiPoints,
                  isCanceled: false,
                })
              )
              .sort(
                (
                  a: SeminarDateCreateUpdateModel,
                  b: SeminarDateCreateUpdateModel
                ) => moment(a.startDate).diff(moment(b.startDate))
              ),
          })
        )
        .filter(
          (group: SeminarDateSeriesCreateUpdateModel) => group.name !== null
        ),
      fileIds: this.fileManagerState.getFiles().map(file => file.id),
      linkIds: this.fileManagerState.getLinks().map(link => link.id),
      sponsorCardIds: this.seminarForm.get('sponsorCards').value,
    };
  }

  /**
   * checks if the form has unsaved changes amd asks the user if he wants to leave the page
   * @returns CanDeactivateType
   */
  public canDeactivate(): CanDeactivateType {
    if (this.isLoading) {
      return true;
    }

    return this.formService.confirmDeactivation(
      this.seminarForm.value,
      this.initialFormValues
    );
  }

  public onPreview() {
    this.previewMode = true;
    const seminarCreateUpdateModel = this.buildSeminarCreateUpdateModel(false);
    this.seminar = {
      ...seminarCreateUpdateModel,
      id: this.seminar?.id,
      title: this.seminarForm.get('name').value,
      lecturer1: this.lecturers.find(
        lecturer => lecturer.id === this.seminarForm.get('lecturer').value
      ),
      lecturer2: this.lecturers.find(
        lecturer => lecturer.id === this.seminarForm.get('lecturer2').value
      ),
      category1: this.categories.find(
        category => category.id === this.seminarForm.get('category').value
      ),
      conditions: seminarCreateUpdateModel.conditions,
      loaded: 0,
      isDraft: false,
      isCanceled: this.seminar?.isCanceled ?? false,
      cancelReason: this.seminar?.cancelReason ?? null,
      dateSeries: seminarCreateUpdateModel.dateSeries.map(
        (dateSeries: SeminarDateSeriesCreateUpdateModel) => ({
          ...dateSeries,
          dates: dateSeries.dates.map((date: SeminarDateCreateUpdateModel) => ({
            ...date,
            isCanceled: false,
            startDate: new Date(date.startDate),
            endDate: new Date(date.endDate),
          })),
        })
      ),
    };
    this.selectedSponsorCards = this.sponsorCardOptions.filter(sponsorCard =>
      this.seminar.sponsorCardIds?.includes(sponsorCard.id)
    );
  }

  public onEdit() {
    this.previewMode = false;
  }

  private cancelSeminar() {
    const dialogRef = this.dialogService.open(CancelSeminarDialogComponent, {
      width: '550px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
    });
    dialogRef.onClose.subscribe(
      (response: { confirmed: boolean; reason: string }) => {
        if (response.confirmed) {
          this.seminarService
            .cancelSeminar(this.seminar.id, response.reason)
            .subscribe({
              next: () => {
                this.location.back();
              },
              error: error => {
                this.alertService.showErrorAlert(
                  'Das Seminar konnte nicht abgesagt werden.'
                );
              },
            });
        }
      }
    );
  }

  public onDeleteSeminar(): void {
    this.seminarService
      .getSeminarDeleteInfo(this.seminar.id)
      .pipe(take(1))
      .subscribe({
        next: deleteInfo => {
          let message =
            'Bist du sicher, dass du dieses Seminar löschen möchtest?';
          let htmlMessage = false;

          if (deleteInfo.pendingDateSeriesCount > 0) {
            const detailsText = deleteInfo.details
              .map(
                detail =>
                  `${detail.dateSeriesName}: ${detail.registrationCount}`
              )
              .join(', ');
            if (deleteInfo.pendingDateSeriesCount > 1) {
              message += `<br><br>Hinweis: Es gibt noch ${deleteInfo.pendingDateSeriesCount} ausstehende Terminreihen`;
            } else {
              message += `<br><br>Hinweis: Es gibt noch ${deleteInfo.pendingDateSeriesCount} ausstehende Terminreihe `;
            }
            if (deleteInfo.totalRegistrationCount > 1) {
              message += ` mit insgesamt ${deleteInfo.totalRegistrationCount} Anmeldungen`;
            } else {
              message += ` mit insgesamt ${deleteInfo.totalRegistrationCount} Anmeldung`;
            }
            message += ` (${detailsText}).<br>Diese werden <strong>nicht</strong> automatisch storniert!`;
            htmlMessage = true;
          }

          const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
            width: '475px',
            contentStyle: { overflow: 'auto' },
            breakpoints: {
              '640px': '90vw',
            },
            data: {
              title: 'Seminar löschen',
              message,
              htmlMessage,
              icon: 'fa-regular fa-trash-can',
              iconColor: 'red',
              confirmButtonText: 'Löschen',
            },
          });

          dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.isLoading = true;
              this.seminarService
                .deleteSeminar(this.seminar.id)
                .pipe(take(1))
                .subscribe({
                  next: () => {
                    this.alertService.showSuccessAlert(
                      'Seminar erfolgreich gelöscht'
                    );
                    this.initialFormValues = this.seminarForm.value;
                    this.router.navigate(['/administration/seminars']);
                  },
                  error: () => {
                    this.isLoading = false;
                    this.alertService.showErrorAlert(
                      'Das Seminar konnte nicht gelöscht werden.'
                    );
                  },
                });
            }
          });
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Die Löschinformationen konnten nicht geladen werden.'
          );
        },
      });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.fileManagerState.clear();
  }
}
